import { AbstractFieldGroupView } from '../base/AbstractFieldGroupView';
import { html } from 'rune-ts';
import { RadioGroup } from '../../../../../renderApp/components/cells/RadioGroup/RadioGroup';
import input_klass from '../../../../../renderApp/components/cells/DateInputField/styles/BaseInputView.module.scss';
import klass from './SaleStartDateInputField.module.scss';
import { font } from '../../../../../shared/typography';
import { BaseInputFactory } from '../../../../../renderApp/components/cells/BaseInput/BaseInputFactory';
import { NumberInputView } from '../../../../../renderApp/components/cells/BaseInput/views/NumberInputView';
import { limited_identifier } from '../../../../features/ProductEditor/const/limited';
import { IVisibleController, VisibleController } from '../../../../../shared/util/VisibleController';
import { find, head, isNumber, isUndefined } from '@fxts/core';
import { IValidation, ProductEditorError } from '../types/type';
import { isValidQuantityLimitInputValue } from '../utils/QuantityLimitHelper';
import { ProductCardHelper } from '../../../../../renderApp/components/cells/ProductCard/ProductCardHelper';

export type QuantityLimitInputValue = {
  quantity: number;
  status: 'public' | 'private';
};

export type QuantityLimitInputResult =
  | {
      quantity: number;
      is_quantity_public: boolean;
    }
  | undefined;

export class QuantityLimitInputField
  extends AbstractFieldGroupView<QuantityLimitInputValue | undefined>
  implements IVisibleController, IValidation
{
  private readonly inputView: NumberInputView;
  private readonly radioGroupView: RadioGroup<boolean>;
  private visibleController: VisibleController;

  private error_message?: string;

  private static readonly minQuantity = 1;
  private static readonly maxQuantity = 1_000;
  private static readonly step = 1;

  public static validateValue(value: unknown): value is QuantityLimitInputValue | undefined {
    return isValidQuantityLimitInputValue(value);
  }

  constructor({ value }: { value: QuantityLimitInputValue | undefined }) {
    const visible = value ? ProductCardHelper.isQuantityLimited({ quantity: value.quantity }) : false;

    super(value, T('한정 판매 수량'), true, undefined, visible);

    this.visibleController = new VisibleController(this, visible);

    this.inputView = BaseInputFactory.create(
      'number',
      value?.quantity,
      {
        name: limited_identifier.quantity,
        min: QuantityLimitInputField.minQuantity,
        max: QuantityLimitInputField.maxQuantity,
        step: QuantityLimitInputField.step,
      },
      {
        klass: input_klass.input,
      },
    );
    const radio_items = [
      { display_name: '수량 공개', id: 'public', value: true, default: true },
      { display_name: '수량 비공개', id: 'private', value: false },
    ];
    this.radioGroupView = new RadioGroup(
      {
        items: radio_items,
        selected_id: value ? find((item) => item.id === value.status, radio_items)?.id : undefined,
      },
      {
        direction: 'horizontal',
        custom_klass: {
          group: klass.radio_group,
          item: font('14_regular'),
        },
      },
    );
  }

  protected body() {
    return html`<div>
      ${this.inputView}
      <span class="${klass.error_message} ${this.isErrorMessageVisible() ? '' : klass.hidden}">
        ${this.error_message}
      </span>
      ${this.radioGroupView}
    </div>`;
  }

  public getValue(): QuantityLimitInputResult {
    const quantity = this.inputView.getValue();
    if (isNumber(quantity) && ProductCardHelper.isQuantityLimited({ quantity })) {
      return {
        quantity,
        is_quantity_public: this.radioGroupView.getSelected()?.value ?? false,
      };
    } else {
      return undefined;
    }
  }

  public clear() {
    this.inputView.setValueAndSync(undefined);
    this.radioGroupView.setSelected('public');
  }

  public setVisible(visible: boolean): this {
    if (visible) {
      this.show();
    }
    this.visibleController.setVisible(visible);
    return this;
  }

  private syncErrorMessage() {
    const error_el = this.element().querySelector(`.${klass.error_message}`);
    if (error_el) {
      (error_el as HTMLElement).innerText = this.error_message || '';
      if (this.isErrorMessageVisible()) {
        error_el.classList.remove(klass.hidden);
        this.inputView.element().classList.add(klass.input_error);
      } else {
        error_el.classList.add(klass.hidden);
        this.inputView.element().classList.remove(klass.input_error);
      }
    }
  }

  private isErrorMessageVisible(): boolean {
    return !!this.error_message;
  }

  public setErrorMessage(message?: string): this {
    this.error_message = message;
    this.syncErrorMessage();
    return this;
  }

  public validate(): ProductEditorError[] {
    const errors = [];

    if (this.isRequired() && this.isVisible() && isUndefined(this.inputView.getValue())) {
      const err = {
        target: this,
        title: '한정 판매 수량 미입력',
        message: '한정 판매 수량을 입력해주세요',
        detail: '한정 판매 수량을 입력해주세요',
      };
      errors.push(err);
    }
    return errors;
  }

  public showValidation(): this {
    const first_error = head(this.validate());
    this.setErrorMessage(first_error?.message);
    return this;
  }

  public isVisible(): boolean {
    return this.visibleController.isVisible();
  }
}
